import { type ElementTransformer } from "@lexical/markdown";
import { $isListItemNode } from "@lexical/list";
import {
  $createParagraphNode,
  $getSiblingCaret,
  $insertNodeToNearestRootAtCaret,
  $isElementNode,
  DecoratorNode,
  ElementNode,
  COMMAND_PRIORITY_LOW,
  type LexicalNode,
  type SerializedLexicalNode,
} from "lexical";
import type { Component } from "vue";
import { h } from "vue";

import HorizontalRule from "./HorizontalRule.vue";

const $liftHorizontalRuleFromList = (node: HorizontalRuleNode) => {
  const parent = node.getParent();
  if ($isListItemNode(parent)) {
    $insertNodeToNearestRootAtCaret(node, $getSiblingCaret(node, "next"), { $shouldSplit: () => false });
    if (parent.isEmpty()) {
      parent.remove();
    }
  }
};

export class HorizontalRuleNode extends DecoratorNode<Component> {
  $config() {
    return this.config("horizontal-rule", { $transform: $liftHorizontalRuleFromList });
  }

  static getType(): string {
    return "horizontal-rule";
  }

  static clone(node: HorizontalRuleNode): HorizontalRuleNode {
    return new HorizontalRuleNode(node.__key);
  }

  // eslint-disable-next-line class-methods-use-this
  createDOM(): HTMLElement {
    const element = document.createElement("div");
    element.classList.add("contents");
    return element;
  }

  // eslint-disable-next-line class-methods-use-this
  updateDOM(): false {
    return false;
  }

  static importJSON(): LexicalNode {
    return new HorizontalRuleNode();
  }

  // eslint-disable-next-line class-methods-use-this
  exportJSON(): SerializedLexicalNode {
    return {
      type: HorizontalRuleNode.getType(),
      version: 1,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  exportDOM() {
    return { element: document.createElement("hr") };
  }

  // eslint-disable-next-line class-methods-use-this
  decorate(): Component {
    return h(HorizontalRule);
  }

  // eslint-disable-next-line class-methods-use-this
  isInline(): false {
    return false;
  }

  // eslint-disable-next-line class-methods-use-this
  isKeyboardSelectable(): false {
    return false;
  }

  // eslint-disable-next-line class-methods-use-this
  getTextContent(): string {
    return "---";
  }
}

export function $createHorizontalRuleNode(): HorizontalRuleNode {
  return new HorizontalRuleNode();
}

export function $isHorizontalRuleNode(node: LexicalNode | null): node is HorizontalRuleNode {
  return node instanceof HorizontalRuleNode;
}

HorizontalRuleNode.importDOM = () => ({
  hr: () => ({
    conversion: () => ({ node: $createHorizontalRuleNode() }),
    priority: COMMAND_PRIORITY_LOW,
  }),
});

export const HORIZONTAL_RULE_TRANSFORMER: ElementTransformer = {
  dependencies: [HorizontalRuleNode],
  export: (node: LexicalNode) => {
    if (!$isHorizontalRuleNode(node)) {
      return null;
    }
    return "---";
  },
  regExp: /^(-{3,}|\*{3,}|—-)$/,
  replace: (parentNode: ElementNode) => {
    const hrNode = $createHorizontalRuleNode();
    parentNode.replace(hrNode);
    $liftHorizontalRuleFromList(hrNode);

    if (hrNode.getNextSibling() === null) {
      const paragraphNode = $createParagraphNode();
      hrNode.insertAfter(paragraphNode);
      paragraphNode.selectStart();
    } else {
      const nextSibling = hrNode.getNextSibling();
      if ($isElementNode(nextSibling)) {
        nextSibling.selectStart();
      }
    }
  },
  type: "element",
};
